import type { ReactNode } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { cn } from '@savvy-kit/lib/utils';

import type { BulkAction } from '../../types/data-table-props';

type SelectionBarProps = {
  readonly selectedIds: string[];
  readonly pageSelectedCount: number;
  readonly pageRowCount: number;
  readonly actions: readonly BulkAction[];
  readonly onCancel: () => void;
  readonly className?: string;
  /** Left slot, before the count — e.g. a select-all control. */
  readonly leading?: ReactNode;
  /** Trails the visible "N of M" count. Defaults to the paginated-table wording. */
  readonly countSuffix?: string;
  /** Live-region announcement for the count. Defaults to `"N of M selected {countSuffix}"`. */
  readonly countAriaLabel?: string;
  /** Landmark name for the bar. Defaults to the paginated-table wording. */
  readonly regionAriaLabel?: string;
};

export function SelectionBar({
  selectedIds,
  pageSelectedCount,
  pageRowCount,
  actions,
  onCancel,
  className,
  leading,
  countSuffix = 'on this page',
  countAriaLabel,
  regionAriaLabel = 'Table selection actions',
}: SelectionBarProps) {
  if (selectedIds.length === 0) return null;

  return (
    <div
      className={cn(
        'fixed left-1/2 -translate-x-1/2 bottom-12 z-50 bg-white border border-light shadow-md rounded-full flex items-center',
        className,
      )}
      role="region"
      aria-label={regionAriaLabel}
    >
      {/* `ReactNode` includes null, so a conditional slot passed as `cond ? <X/> : null`
          must not reserve the 16px of padding. */}
      {leading !== undefined && leading !== null && <div className="pl-4">{leading}</div>}
      <div
        className="text-secondary px-3 py-2.5 text-sm min-w-36 text-center"
        aria-live="polite"
        aria-atomic="true"
        // Derived from countSuffix so a caller that only overrides the visible wording
        // can't ship an announcement that still says "on this page". "selected" is
        // interpolated separately because the visible text omits the verb.
        aria-label={countAriaLabel ?? `${pageSelectedCount} of ${pageRowCount} selected ${countSuffix}`}
      >
        <b className="text-dark">{pageSelectedCount}</b> of <b className="text-dark">{pageRowCount}</b> {countSuffix}
      </div>
      {actions.map((action) => {
        const isDisabled = action.disabled?.(selectedIds) ?? false;
        const reason = isDisabled ? action.disabledReason?.(selectedIds) : undefined;

        const button = (
          <Button
            variant="ghost"
            size="default"
            disabled={isDisabled}
            className={cn(
              'flex h-10 py-2 px-4 justify-center items-center gap-2 rounded-none',
              action.destructive === true && 'text-destructive',
            )}
            icon={action.icon}
            onClick={() => action.onClick(selectedIds)}
          >
            {action.label}
          </Button>
        );

        return (
          <div key={action.label} className="border-l border-light">
            {reason === undefined ? (
              button
            ) : (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span tabIndex={0} className="flex">
                      {button}
                    </span>
                  }
                />
                <TooltipContent side="top">{reason}</TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      })}
      <div className="border-l border-light">
        <Button
          variant="ghost"
          size="default"
          className="flex h-10 py-2 px-4 justify-center items-center gap-2 rounded-r-full"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
